import { Component, Input, signal } from '@angular/core';
import { BaseStepComponent } from '../base-step/base-step.component';
import { CreditFacilityForm, ExpenseDetailForm, LivingExpensesForm, VehicleIndividualForm } from '../../../../types/v4/vehicleIndividualSteps';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { VehicleApplicationForm } from '../../../../types/v4/vehicle-application-types';
import { CommonModule } from '@angular/common';
import { SharedModuleV4 } from '../../../../shared/sharedv4.module';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { FormButtonGroupSimpleComponent } from '../../../shared/form-button-group-simple/form-button-group-simple.component';
import { FormFieldSelectComponent, SelectListType } from '../../../shared/form-field-select/form-field-select.component';
import { MatIconModule } from '@angular/material/icon';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormFieldCurrencyComponent } from '../../../shared/form-field-currency/form-field-currency.component';
import { ApplicationExpenseType } from '../../../../api/v4/models/CommonUtilities/application-expense-type';
import { PeriodTypeEnum } from '../../../../api/v4/models/Models/Application/FinancialDetails/period-type-enum';
import { conditionalRequiredValidator } from '../../../../types/validators';
import { UserAction } from '../../../../api/v4/models/CommonUtilities/user-action';
import { PublicPaymentDetailApplicationExpenseType } from '../../../../api/v4/models/Models/v4/Application/Public/Financial/public-payment-detail-application-expense-type';
import { VehicleAppService } from '../../../../services/v4/vehicle-app.service';
import { MaritalStatusEnum } from '../../../../api/v4/models/Models/v4/Application/PersonalDetails/marital-status-enum';
import { startWith, pairwise } from 'rxjs';
import { LivingSituationEnum } from '../../../../api/v4/models/Models/v4/Application/PersonalDetails/living-situation-enum';

@Component({
  selector: 'app-living-expenses',
  host: { 'data-version': 'v4' },
  standalone: true,
  imports: [
    CommonModule,
    SharedModuleV4,
    MatStepperModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSnackBarModule,
    MatCardModule,
    MatButtonToggleModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatSelectModule,
    MatOptionModule,
    FormButtonGroupSimpleComponent,
    FormFieldSelectComponent,
    MatIconModule,
    FormFieldCurrencyComponent
  ],
  templateUrl: './living-expenses.component.html',
  styleUrl: './living-expenses.component.scss'
})

export class LivingExpensesComponent extends BaseStepComponent {

  @Input() parentStepper!: MatStepper;
  @Input() fg!: FormGroup<LivingExpensesForm>;  
  @Input() formSubmitted!: boolean;

  ApplicationExpenseType = ApplicationExpenseType;
  PublicPaymentDetailApplicationExpenseType = PublicPaymentDetailApplicationExpenseType;
  UserAction = UserAction;
  SelectListType = SelectListType;

  individualHeaderDisplay: string = '';
  hasPartner: boolean = false;

  totalLivingExpenses = signal(0);
  totalOtherExpenses = signal(0);

  constructor(vehicleAppService: VehicleAppService) {
    super(vehicleAppService);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    if (this.fg) {

      // Subscribe to all relevant value changes
      const updateLivingExpensesTotal = () => this.totalLivingExpenses.set(this.calculateTotalLivingExpenses());
      this.subscriptions.push(
        this.fg.controls.billsUtilitiesExpense.valueChanges.subscribe(updateLivingExpensesTotal),
        this.fg.controls.foodGroceriesExpense.valueChanges.subscribe(updateLivingExpensesTotal),
        this.fg.controls.transportExpense.valueChanges.subscribe(updateLivingExpensesTotal),
        this.fg.controls.phoneInternetExpense.valueChanges.subscribe(updateLivingExpensesTotal),
        this.fg.controls.clothingShoesPersonalCareExpense.valueChanges.subscribe(updateLivingExpensesTotal),
        this.fg.controls.insuranceExpense.valueChanges.subscribe(updateLivingExpensesTotal),
        this.fg.controls.otherExpenses.valueChanges.subscribe(updateLivingExpensesTotal)
      );      
      // Initial calculation
      updateLivingExpensesTotal();

       // Subscribe to other expenses value changes
      const updateOtherExpensesTotal = () => this.totalOtherExpenses.set(this.calculateTotalOtherExpenses());
      this.subscriptions.push(
        this.fg.controls.otherExpenses.valueChanges.subscribe(updateOtherExpensesTotal)
      );      
      // Initial calculation
      updateOtherExpensesTotal();


      // Subscriptions to update frequency validity when amount changes
      this.subscriptions.push(
        this.fg.controls.billsUtilitiesExpense.controls.amount.valueChanges.subscribe(() => {
          this.fg.controls.billsUtilitiesExpense.controls.frequency.updateValueAndValidity();
        }),
        this.fg.controls.foodGroceriesExpense.controls.amount.valueChanges.subscribe(() => {
          this.fg.controls.foodGroceriesExpense.controls.frequency.updateValueAndValidity();
        }),
        this.fg.controls.transportExpense.controls.amount.valueChanges.subscribe(() => {
          this.fg.controls.transportExpense.controls.frequency.updateValueAndValidity();
        }),
        this.fg.controls.phoneInternetExpense.controls.amount.valueChanges.subscribe(() => {
          this.fg.controls.phoneInternetExpense.controls.frequency.updateValueAndValidity();
        }),
        this.fg.controls.clothingShoesPersonalCareExpense.controls.amount.valueChanges.subscribe(() => {
          this.fg.controls.clothingShoesPersonalCareExpense.controls.frequency.updateValueAndValidity();
        }),
        this.fg.controls.insuranceExpense.controls.amount.valueChanges.subscribe(() => {
          this.fg.controls.insuranceExpense.controls.frequency.updateValueAndValidity();
        }),

        this.fg.controls.partnerNetIncome.valueChanges.subscribe(() => {
          this.fg.controls.partnerNetIncomeFrequency.updateValueAndValidity();
        }),

      );

      this.fg.controls.hasOtherExpenses.valueChanges
        .pipe(
          startWith(this.fg.controls.hasOtherExpenses.value), // Emit the initial value
          pairwise() // Emit the previous and current values as an array
        )
        .subscribe(([oldValue, newValue]) => {
          if (newValue && newValue != oldValue) {
            if (newValue == UserAction.No) {
              this.fg.controls.otherExpenses.clear();
            } else {
              if (this.fg.controls.otherExpenses.length === 0) {
                this.addExpense();
              }
            }
          }
        });


      // Listen for when the primary applicant is configured
      this.subscriptions.push(this.vehicleAppService.indivIndividualSubject$.subscribe((value: FormGroup<VehicleIndividualForm> | null) => {
        console.log('Individual form group updated:', value);

        // Check first individual's marital status to determine if partner fields should be enabled
        value?.controls.step2.controls.maritalStatus.valueChanges.subscribe((value: any) => {
          console.log('Marital status changed to:', value);

          // if it is not a joint application, and married or de facto, then has partner is true
          // and need to ask for shared expenses and partner net income
          this.hasPartner = !this.vehicleAppService.isJointApplication() && !this.vehicleAppService.isCommericial() &&
            (value == MaritalStatusEnum.Married || value == MaritalStatusEnum.DeFacto);

          if (this.hasPartner) {
            this.fg.controls.expensesSharedWithPartner.enable();
          } else {
            this.fg.controls.expensesSharedWithPartner.disable();
            this.fg.controls.expensesSharedWithPartner.setValue(null);
          }
        });


        // Add the disabled Rates expense if the user is a homeowner
        value?.controls.step1.controls.addressPhysical.controls.livingSituation.valueChanges
          .pipe(
            startWith(value?.controls.step1.controls.addressPhysical.controls.livingSituation.value), // Emit the initial value
            pairwise() // Emit the previous and current values as an array
          )
          .subscribe(([oldValue, newValue]) => {
            if (newValue && newValue != oldValue) {
              const ratesExpenseIndex = this.fg.controls.otherExpenses.controls.findIndex(x => x.controls.type.value === PublicPaymentDetailApplicationExpenseType.Rates);

              if (newValue === LivingSituationEnum.HomeownerWithoutMortgage || newValue === LivingSituationEnum.Mortgaged) {
                if (ratesExpenseIndex === -1) {
                  this.addExpense(ApplicationExpenseType.Rates, null, null);
                }

                this.fg.controls.hasOtherExpenses.setValue(UserAction.Yes);
                this.fg.controls.hasOtherExpenses.disable();

                // if (this.parentFg.controls.step1.controls.isAddressProvidedByPartner.value == UserAction.No) {
                //   this.fg.controls.hasNonSharedPartnerExpenses.setValue(UserAction.Yes);
                //   this.fg.controls.hasNonSharedPartnerExpenses.disable();
                // }

              } else {
                if (ratesExpenseIndex !== -1) {
                  this.removeExpense(ratesExpenseIndex);
                  this.fg.controls.hasOtherExpenses.enable();

                  if (this.fg.controls.otherExpenses.length === 0) {
                    this.fg.controls.hasOtherExpenses.setValue(UserAction.No);
                  }

                  // if (this.parentFg.controls.step1.controls.isAddressProvidedByPartner.value == UserAction.Yes) {
                  //   this.fg.controls.hasNonSharedPartnerExpenses.enable();
                  // }
                }
              }
            }
          });

      }));

      // When expensesSharedWithPartner changes, enable or disable partner net income fields
      this.subscriptions.push(this.fg.controls.expensesSharedWithPartner.valueChanges.subscribe((value) => {
        console.log('expensesSharedWithPartner changed to:', value);
        if (value == UserAction.Yes) {
          this.enableControls([this.fg.controls.partnerNetIncome, this.fg.controls.partnerNetIncomeFrequency]);
        }
        else {
          this.disableControls([this.fg.controls.partnerNetIncome, this.fg.controls.partnerNetIncomeFrequency]);
          this.fg.controls.partnerNetIncome.setValue(null);
        }
      }));
    }
  }

  get appForm(): VehicleApplicationForm {
    return this.vehicleAppService.getApplicationFg();
  }

  /**
   * Calculates the total monthly living expenses from all fields.
   */
  private calculateTotalLivingExpenses(): number {
    if (!this.fg) return 0;
    let total = 0;
    const controls = this.fg.controls;
    // Main expense fields
    total += this.vehicleAppService.toMonthly(controls.billsUtilitiesExpense.controls.amount.value, controls.billsUtilitiesExpense.controls.frequency.value);
    total += this.vehicleAppService.toMonthly(controls.foodGroceriesExpense.controls.amount.value, controls.foodGroceriesExpense.controls.frequency.value);
    total += this.vehicleAppService.toMonthly(controls.transportExpense.controls.amount.value, controls.transportExpense.controls.frequency.value);
    total += this.vehicleAppService.toMonthly(controls.phoneInternetExpense.controls.amount.value, controls.phoneInternetExpense.controls.frequency.value);
    total += this.vehicleAppService.toMonthly(controls.clothingShoesPersonalCareExpense.controls.amount.value, controls.clothingShoesPersonalCareExpense.controls.frequency.value);
    total += this.vehicleAppService.toMonthly(controls.insuranceExpense.controls.amount.value, controls.insuranceExpense.controls.frequency.value);
    
    return total;
  }


   /**
   * Calculates the total other expenses from all fields.
   */
  private calculateTotalOtherExpenses(): number {
    if (!this.fg) return 0;
    let total = 0;
    const controls = this.fg.controls;
    
    // Other expenses (array)
    if (controls.otherExpenses && Array.isArray(controls.otherExpenses.controls)) {
      for (const group of controls.otherExpenses.controls) {
        total += this.vehicleAppService.toMonthly(group.controls.amount.value, group.controls.frequency.value);
      }
    }

    return total;
  }



  removeExpense(index: number): void {
    this.fg.controls.otherExpenses.removeAt(index);
    if (index == 0) {
      this.fg.controls.hasOtherExpenses.setValue(UserAction.No);
    }
  }

  addExpense(expenseType: ApplicationExpenseType | null = null, amount: number | null = null, frequency: PeriodTypeEnum | null = null): FormGroup<ExpenseDetailForm> {
    var exp = this.createExpenseGroup(expenseType, amount, frequency);
    this.fg.controls.otherExpenses.push(exp);
    return exp;
  }

  createExpenseGroup(expenseType: ApplicationExpenseType | null, amount: number | null, frequency: PeriodTypeEnum | null): FormGroup<ExpenseDetailForm> {
    var idFg = new FormGroup<ExpenseDetailForm>({
      type: new FormControl<ApplicationExpenseType | null>({ value: expenseType, disabled: expenseType != null }, Validators.required),
      amount: new FormControl<number | null>(null, Validators.required),
      frequency: new FormControl<PeriodTypeEnum | null>(null, Validators.required),
      description: new FormControl<string | null>(null, conditionalRequiredValidator('type', ApplicationExpenseType.Other))
    } as ExpenseDetailForm);

    return idFg;
  }
}
